import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { InfoIcon, XIcon } from "lucide-react";
import { useConsoleApi } from "./useConsoleApi";
import { SEVERITIES, type CaAssetsResp, type Severity } from "./types";
import { MonoTag, SEV_STYLE, SeverityTag, fmtDateTime, fmtMiB, fmtNum, timeAgo } from "./utils";
import { EmptyState } from "./FindingsTab";

const PAGE_SIZE = 100;
const THRESHOLD_HINTS: Partial<Record<Severity, string>> = {
    low: 'Above FXServer\'s warning limit. Usually fine alone, but adds up.',
    medium: 'Heavy. Worth optimizing, especially for textures (.ytd).',
    high: 'Very heavy. Likely to cause texture loss on mid-range clients.',
    critical: 'FXServer flags it in red: will cause streaming issues.',
};


function StatBox({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
    return (
        <div className="rounded-lg border bg-card px-4 py-3">
            <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="truncate font-mono text-xl font-semibold">{value}</div>
            {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
        </div>
    );
}


export default function AssetsTab({ bootId, refreshMs, now, resource, setResource }: {
    bootId: string;
    refreshMs: number;
    now: number;
    resource?: string;
    setResource: (r: string | undefined) => void;
}) {
    const [severity, setSeverity] = useState<Severity | undefined>(undefined);
    const [memory, setMemory] = useState<'physical' | 'virtual' | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [limit, setLimit] = useState(PAGE_SIZE);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);
    useEffect(() => setLimit(PAGE_SIZE), [bootId, resource, severity, memory, debouncedSearch]);

    const resp = useConsoleApi<CaAssetsResp>('assets', {
        boot: bootId,
        resource,
        severity,
        memory,
        search: debouncedSearch,
        limit,
    }, refreshMs);
    const data = resp.data;

    if (resp.error) return <EmptyState>Failed to load assets: {(resp.error as Error).message}</EmptyState>;
    if (!data) return <EmptyState>Loading…</EmptyState>;

    const maxSize = Math.max(1, ...data.items.map((a) => a.sizeMiB), data.stats.largest?.sizeMiB ?? 0);
    const hasFilter = !!(resource || severity || memory || debouncedSearch);

    return (
        <div className="flex flex-col gap-4">
            {/* Explanation */}
            <div className="flex gap-3 rounded-lg border border-info/40 bg-info/5 px-4 py-3 text-sm">
                <InfoIcon className="mt-0.5 size-4 shrink-0 text-info" />
                <div className="space-y-1">
                    <p>
                        When a resource starts, FXServer warns about every streamed asset that uses more than <b>16 MiB</b> of memory.
                        Oversized assets can and <b>will</b> lead to streaming issues: models not loading or rendering, texture loss,
                        and crashes on lower-end clients.
                    </p>
                    <p className="text-muted-foreground">
                        <b>Physical</b> memory is the GPU part (mostly textures, <code>.ytd</code> and embedded textures),
                        <b> virtual</b> memory is the system memory part (models: <code>.yft</code>, <code>.ydr</code>, <code>.ydd</code>).
                        Resize textures (2048px or less is usually enough), use DXT compression, and drop unused LODs or <code>_hi</code> models when possible.
                    </p>
                </div>
            </div>

            {/* Thresholds */}
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                {data.thresholds.slice().reverse().map((t) => {
                    const s = SEV_STYLE[t.severity];
                    const active = severity === t.severity;
                    return (
                        <button
                            key={t.severity}
                            onClick={() => setSeverity(active ? undefined : t.severity)}
                            className={cn(
                                'flex flex-col items-start gap-1 rounded-lg border border-t-4 bg-card px-4 py-3 text-left hover:bg-muted/30',
                                s.border,
                                active && 'ring-1 ring-foreground/40',
                            )}
                        >
                            <div className="flex w-full items-baseline justify-between">
                                <span className={cn('text-2xs font-semibold uppercase tracking-wider', s.text)}>{t.severity}</span>
                                <span className={cn('font-mono text-lg font-semibold', data.stats.counts[t.severity] ? s.text : 'text-muted-foreground')}>
                                    {fmtNum(data.stats.counts[t.severity])}
                                </span>
                            </div>
                            <span className="font-mono text-sm">&gt; {t.minMiB} MiB</span>
                            <span className="text-xs text-muted-foreground">{THRESHOLD_HINTS[t.severity]}</span>
                        </button>
                    );
                })}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <StatBox label="Oversized assets" value={fmtNum(data.stats.count)} />
                <StatBox label="Total size" value={fmtMiB(data.stats.totalMiB)} />
                <StatBox label="Resources" value={fmtNum(data.stats.resources)} />
                <StatBox
                    label="Largest"
                    value={data.stats.largest ? fmtMiB(data.stats.largest.sizeMiB) : '--'}
                    sub={data.stats.largest ? `${data.stats.largest.resource}/${data.stats.largest.file}` : undefined}
                />
            </div>

            {!data.stats.count ? (
                <EmptyState>No oversized asset reported by FXServer in this boot.</EmptyState>
            ) : (
                <>
                    {/* Totals per resource */}
                    <div className="flex flex-col gap-2">
                        <h3 className="text-sm font-semibold">Totals per resource</h3>
                        <div className="max-h-[22rem] overflow-auto rounded-lg border bg-card">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-card">
                                    <tr className="border-b text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        <th className="px-3 py-2">Resource</th>
                                        <th className="px-3 py-2">Worst</th>
                                        <th className="px-3 py-2 text-right">Assets</th>
                                        <th className="px-3 py-2 text-right">Physical</th>
                                        <th className="px-3 py-2 text-right">Virtual</th>
                                        <th className="px-3 py-2 text-right">Total</th>
                                        <th className="px-3 py-2">Largest</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.perResource.map((r) => (
                                        <tr
                                            key={r.resource}
                                            onClick={() => setResource(resource === r.resource ? undefined : r.resource)}
                                            className={cn(
                                                'cursor-pointer border-b last:border-b-0 hover:bg-muted/30',
                                                resource === r.resource && 'bg-muted/50',
                                            )}
                                        >
                                            <td className="px-3 py-1.5 font-mono text-xs">{r.resource}</td>
                                            <td className="px-3 py-1.5"><SeverityTag severity={r.worst} /></td>
                                            <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtNum(r.assets)}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-xs">{r.physicalMiB ? fmtMiB(r.physicalMiB) : '·'}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-xs">{r.virtualMiB ? fmtMiB(r.virtualMiB) : '·'}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold">{fmtMiB(r.totalMiB)}</td>
                                            <td className="max-w-[16rem] truncate px-3 py-1.5 font-mono text-xs text-muted-foreground" title={r.largest.file}>
                                                <span className={SEV_STYLE[r.largest.severity].text}>{fmtMiB(r.largest.sizeMiB)}</span> {r.largest.file}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Assets */}
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="mr-2 text-sm font-semibold">Assets</h3>
                            {resource && (
                                <MonoTag className="flex items-center gap-1" onClick={() => setResource(undefined)} title="Remove filter">
                                    {resource} <XIcon className="size-3" />
                                </MonoTag>
                            )}
                            <div className="flex gap-1">
                                {([undefined, 'physical', 'virtual'] as const).map((m) => (
                                    <button
                                        key={m ?? 'all'}
                                        onClick={() => setMemory(m)}
                                        className={cn(
                                            'rounded-sm border px-2 py-1 text-xs',
                                            memory === m ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted',
                                        )}
                                    >
                                        {m ?? 'All memory'}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-1">
                                {SEVERITIES.filter((s) => s !== 'info').map((sev) => (
                                    <button
                                        key={sev}
                                        onClick={() => setSeverity(severity === sev ? undefined : sev)}
                                        className={cn(
                                            'rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wider',
                                            severity === sev
                                                ? cn(SEV_STYLE[sev].bg, SEV_STYLE[sev].text, SEV_STYLE[sev].border)
                                                : 'text-muted-foreground hover:bg-muted',
                                        )}
                                    >
                                        {sev}
                                    </button>
                                ))}
                            </div>
                            <Input
                                className="h-8 w-56"
                                placeholder="Search file or resource…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {hasFilter && (
                                <Button
                                    size="sm" variant="ghost" className="h-8"
                                    onClick={() => { setResource(undefined); setSeverity(undefined); setMemory(undefined); setSearch(''); }}
                                >
                                    Clear filters
                                </Button>
                            )}
                            <span className="ml-auto text-xs text-muted-foreground">
                                {fmtNum(Math.min(data.items.length, data.total))} of {fmtNum(data.total)} assets
                            </span>
                        </div>
                        {!data.items.length ? (
                            <EmptyState>No asset matches these filters.</EmptyState>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border bg-card">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                                            <th className="px-3 py-2">Asset</th>
                                            <th className="w-56 px-3 py-2">Size</th>
                                            <th className="px-3 py-2">Ext</th>
                                            <th className="px-3 py-2">Memory</th>
                                            <th className="px-3 py-2">Severity</th>
                                            <th className="px-3 py-2">Last seen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.items.map((a) => (
                                            <tr key={`${a.resource}/${a.file}/${a.memory}`} className="border-b last:border-b-0 hover:bg-muted/30">
                                                <td className="max-w-[28rem] px-3 py-1.5">
                                                    <div className="truncate font-mono text-xs" title={a.file}>{a.file}</div>
                                                    <button
                                                        className="truncate font-mono text-2xs text-muted-foreground hover:underline"
                                                        onClick={() => setResource(a.resource)}
                                                    >
                                                        {a.resource}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-1.5 flex-1 overflow-hidden rounded-[1px] bg-muted">
                                                            <div
                                                                className={cn('h-full', SEV_STYLE[a.severity].bar)}
                                                                style={{ width: `${Math.min(100, (a.sizeMiB / maxSize) * 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className={cn('w-16 text-right font-mono text-xs', SEV_STYLE[a.severity].text)}>
                                                            {fmtMiB(a.sizeMiB)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5 font-mono text-xs">.{a.ext}</td>
                                                <td className="px-3 py-1.5 text-xs text-muted-foreground">{a.memory}</td>
                                                <td className="px-3 py-1.5"><SeverityTag severity={a.severity} /></td>
                                                <td className="px-3 py-1.5 text-xs text-muted-foreground" title={fmtDateTime(a.lastSeen)}>
                                                    {timeAgo(a.lastSeen, now)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {data.total > data.items.length && (
                            <div className="flex justify-center">
                                <Button size="sm" variant="outline" onClick={() => setLimit(limit + PAGE_SIZE)}>
                                    Load more ({fmtNum(data.total - data.items.length)} remaining)
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
